import type { FormDataModel } from 'components/Form';
import type { PricingStrategyValues } from 'components/PricingStrategySection';
import { formatTimeForApi, parseTimeValue } from 'components/TimeInput/TimeInput';
import { isCustomizePackage, normalizeCustomPricePackageName } from 'utils/pricePackage';

export type DraftLineItemCostingContext = {
  formData: FormDataModel;
  pricing: PricingStrategyValues;
  bookedAddOns: any[];
  hourlyPricing: boolean;
  pricePackageList: any[];
};

const isTimeValid = (time: unknown) => parseTimeValue(time as string) != null;

/** True when a select/drop-down has a real option (empty `{}` is not filled). */
const hasSelection = (value: unknown): boolean => {
  if (value == null || value === '') return false;
  if (typeof value === 'object') {
    const selected = (value as { value?: unknown }).value;
    return selected != null && selected !== '';
  }
  return true;
};

const hasGuests = (value: unknown): boolean => {
  if (value == null || value === '') return false;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0;
};

export type LineItemCostingLabels = {
  venue: string;
  startDate: string;
  endDate: string;
  guests: string;
  partOfDay: string;
  status: string;
  startTime: string;
  endTime: string;
  pricePackage: string;
};

/** Labels for line-item fields that are still empty before costing. */
export const missingLineItemCostingLabels = (
  item: Record<string, any> | null | undefined,
  hourlyPricing: boolean,
  labels: LineItemCostingLabels,
): string[] => {
  if (!item) return [labels.venue];
  const missing: string[] = [];
  if (!hasSelection(item.venue) && item.venueId == null) missing.push(labels.venue);
  if (!item.startDate) missing.push(labels.startDate);
  if (!item.endDate) missing.push(labels.endDate);
  if (!hasGuests(item.guests)) missing.push(labels.guests);
  if (!hasSelection(item.partOfDay)) missing.push(labels.partOfDay);
  if (!hasSelection(item.status)) missing.push(labels.status);
  if (!isTimeValid(item.startTime)) missing.push(labels.startTime);
  if (!isTimeValid(item.endTime)) missing.push(labels.endTime);
  if (!hourlyPricing && !hasSelection(item.pricePackage) && item.pricePackageId == null) {
    missing.push(labels.pricePackage);
  }
  return missing;
};

/** Line-item fields required before calling `/new-booking/costing`. */
export const isLineItemReadyForCosting = (
  item: Record<string, any> | null | undefined,
  hourlyPricing = false,
): boolean => {
  if (!item) return false;
  if (!hasSelection(item.venue) && item.venueId == null) return false;
  if (!item.startDate || !item.endDate) return false;
  if (!hasGuests(item.guests)) return false;
  if (!hasSelection(item.partOfDay)) return false;
  if (!hasSelection(item.status)) return false;
  if (!isTimeValid(item.startTime) || !isTimeValid(item.endTime)) return false;
  if (!hourlyPricing && !hasSelection(item.pricePackage) && item.pricePackageId == null) return false;
  return true;
};

export const extractCostingErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error_description ||
  error?.response?.data?.message ||
  error?.response?.data?.errors?.[0]?.defaultMessage ||
  error?.message ||
  fallback;

/** Builds a line-item payload matching the save/costing API shape from in-progress form state. */
export function buildLineItemPayloadForCosting(ctx: DraftLineItemCostingContext): Record<string, any> | null {
  const { formData, pricing, bookedAddOns, hourlyPricing, pricePackageList } = ctx;

  if (!isLineItemReadyForCosting(formData, hourlyPricing)) return null;

  return {
    ...formData,
    eventSubtype:
      typeof formData.eventSubtype === 'object'
        ? (formData.eventSubtype as any)?.label || (formData.eventSubtype as any)?.value || ''
        : formData.eventSubtype,
    startTime: formatTimeForApi(formData.startTime as string),
    endTime: formatTimeForApi(formData.endTime as string),
    pricePackage: hourlyPricing ? null : formData.pricePackage,
    customPricePackageName:
      !hourlyPricing && isCustomizePackage(formData.pricePackage, pricePackageList)
        ? normalizeCustomPricePackageName(formData.customPricePackageName)
        : null,
    addOns: bookedAddOns,
    serviceCharge:
      (formData.serviceCharge as any)?.hasOwnProperty?.('value') === null ? null : formData.serviceCharge,
    pricePerPerson: pricing.overridePricePerPerson ? pricing.pricePerPerson : null,
    minimumRevenue: pricing.overrideMinimumRevenue ? pricing.minimumRevenue : null,
    hallRental: pricing.overrideHallRental ? pricing.hallRental : null,
    pricePerHour: hourlyPricing && pricing.overridePricePerHour ? pricing.pricePerHour : null,
    overridePricePerPerson: pricing.overridePricePerPerson,
    overrideMinimumRevenue: pricing.overrideMinimumRevenue,
    overrideHallRental: pricing.overrideHallRental,
    overridePricePerHour: pricing.overridePricePerHour,
  };
}

/** Debounce before re-fetching Event Summary while the user edits the line-item form. */
export const VENUE_DETAILS_COSTING_DEBOUNCE_MS = 1500;

/** Returns only the line item being edited or added (not the full booking list). */
export function resolveCurrentLineItemForCosting(
  lineItems: any[],
  draft: Record<string, any> | null,
  selectedRowId: number | null,
): any[] {
  if (selectedRowId !== null && selectedRowId >= 0 && selectedRowId < lineItems.length) {
    const saved = lineItems[selectedRowId];
    return draft ? [{ ...saved, ...draft }] : [saved];
  }
  return draft ? [draft] : [];
}
